import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { handleAxiosError } from "app/services/app.error.handler";

/**
 * Orchestrates a server-paginated Master Data grid: reads the entity's page slice,
 * fetches a page on demand, and exposes the pagination/sort/search handlers that
 * PaginatedDataGrid expects. Generalises the logic originally inlined in AllUsersView.
 *
 * @param {Function} selector       (state) => entity page slice
 * @param {Object}   actions        the slice's actions (from createPageSlice)
 * @param {Function} fetchFn        ({page,pageSize,sortBy,sortDirection,searchQuery}) => axios promise
 *                                   resolving to a PageResponse on response.data
 * @returns grid state + fetchPage + handlers
 */
export const useServerPaginatedGrid = ({ selector, actions, fetchFn }) => {
    const dispatch = useDispatch();
    const { rows, totalElements, page, pageSize, sortBy, sortDirection, searchQuery, loading } =
        useSelector(selector);

    // Guards against out-of-order responses when the user rapidly changes page/size/sort:
    // only the most recently dispatched fetch is allowed to update the slice.
    const requestSeqRef = useRef(0);

    const fetchPage = useCallback(
        (overrides = {}) => {
            const params = {
                page: overrides.page ?? page,
                pageSize: overrides.pageSize ?? pageSize,
                sortBy: overrides.sortBy ?? sortBy,
                sortDirection: overrides.sortDirection ?? sortDirection,
                searchQuery: overrides.searchQuery ?? searchQuery,
            };
            const seq = ++requestSeqRef.current;
            dispatch(actions.setLoading(true));
            fetchFn(params)
                .then((response) => {
                    if (seq !== requestSeqRef.current) return;
                    const body = response.data || {};
                    dispatch(
                        actions.replacePage({
                            rows: Array.isArray(body.content) ? body.content : [],
                            totalElements: typeof body.totalElements === "number" ? body.totalElements : 0,
                            page: typeof body.page === "number" ? body.page : params.page,
                            pageSize: typeof body.pageSize === "number" ? body.pageSize : params.pageSize,
                        })
                    );
                })
                .catch((error) => {
                    if (seq !== requestSeqRef.current) return;
                    dispatch(
                        actions.replacePage({
                            rows: [],
                            totalElements: 0,
                            page: params.page,
                            pageSize: params.pageSize,
                        })
                    );
                    handleAxiosError(error);
                });
        },
        [dispatch, actions, fetchFn, page, pageSize, sortBy, sortDirection, searchQuery]
    );

    useEffect(() => {
        fetchPage();
        // Reset the cached search when leaving the screen so the next visit starts clean.
        return () => {
            dispatch(actions.setSearchQuery(""));
        };
        // Only fire on mount; subsequent fetches go through the handlers below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePaginationModelChange = (model) => {
        if (model.page === page && model.pageSize === pageSize) return;
        dispatch(actions.setPagination({ page: model.page, pageSize: model.pageSize }));
        fetchPage({ page: model.page, pageSize: model.pageSize });
    };

    const handleSortModelChange = ({ sortBy: nextSortBy, sortDirection: nextSortDirection }) => {
        if (nextSortBy === sortBy && nextSortDirection === sortDirection) return;
        dispatch(actions.setSort({ sortBy: nextSortBy, sortDirection: nextSortDirection }));
        fetchPage({ page: 0, sortBy: nextSortBy, sortDirection: nextSortDirection });
    };

    const handleSearchChange = (nextQuery) => {
        const normalized = (nextQuery ?? "").trim();
        if (normalized === (searchQuery ?? "")) return;
        dispatch(actions.setSearchQuery(normalized));
        fetchPage({ page: 0, searchQuery: normalized });
    };

    return {
        rows,
        totalElements,
        page,
        pageSize,
        sortBy,
        sortDirection,
        searchQuery,
        loading,
        fetchPage,
        handlePaginationModelChange,
        handleSortModelChange,
        handleSearchChange,
    };
};

export default useServerPaginatedGrid;
